import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type Term = {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  academic_year_id: string;
};

type AcademicYear = {
  id: string;
  name: string;
  is_active: boolean;
  terms: Term[];
};

interface PeriodCtx {
  years: AcademicYear[];
  selectedYearId: string | null;
  setSelectedYearId: (id: string) => void;
  selectedTermId: string | null;
  setSelectedTermId: (id: string | null) => void;
  selectedYear: AcademicYear | null;
  selectedTerm: Term | null;
  isLoading: boolean;
}

const Ctx = createContext<PeriodCtx | undefined>(undefined);

export function SchoolHeadPeriodProvider({ children }: { children: React.ReactNode }) {
  const { data: years = [], isLoading } = useQuery({
    queryKey: ["sh-academic-years"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("academic_years")
        .select("id,name,is_active,terms(id,name,start_date,end_date,is_active,academic_year_id)")
        .order("name", { ascending: false });
      if (error) throw error;
      return (data || []) as AcademicYear[];
    },
  });

  const [selectedYearId, setSelectedYearId] = useState<string | null>(null);
  const [selectedTermId, setSelectedTermId] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedYearId && years.length) {
      const active = years.find((y) => y.is_active) || years[0];
      setSelectedYearId(active.id);
      const activeTerm = active.terms?.find((t) => t.is_active) || active.terms?.[0];
      setSelectedTermId(activeTerm?.id || null);
    }
  }, [years, selectedYearId]);

  const selectedYear = useMemo(
    () => years.find((y) => y.id === selectedYearId) || null,
    [years, selectedYearId]
  );
  const selectedTerm = useMemo(
    () => selectedYear?.terms?.find((t) => t.id === selectedTermId) || null,
    [selectedYear, selectedTermId]
  );

  const handleSetYear = (id: string) => {
    setSelectedYearId(id);
    const y = years.find((yr) => yr.id === id);
    const t = y?.terms?.find((tm) => tm.is_active) || y?.terms?.[0];
    setSelectedTermId(t?.id || null);
  };

  return (
    <Ctx.Provider
      value={{
        years,
        selectedYearId,
        setSelectedYearId: handleSetYear,
        selectedTermId,
        setSelectedTermId,
        selectedYear,
        selectedTerm,
        isLoading,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useSchoolHeadPeriod() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSchoolHeadPeriod must be used within SchoolHeadPeriodProvider");
  return ctx;
}
